import { useMemo } from 'react';
import { Modal } from '../Modal';
import { AskAgentButton } from '../AskAgentButton';
import {
  btnSecondarySm,
  tableCell,
  tableCellNum,
  tableCls,
  tableHead,
  tableHeadCell,
  tableHeadCellRight,
  tableRow,
} from '../../lib/styles';
import { useBalanceChanges, useUnmatched } from '../../api/hooks';
import {
  CHANGES_LOG_ID,
  CHANGES_LOG_OPEN_EVENT,
  LS_KEY,
  SOURCE_BADGE,
  deltaCls,
  fmtDelta,
} from './BalanceChangesLog';

/** Сколько строк грузить за раз — окно точки графика обычно узкое (1 бакет ± сверка), 100 с запасом. */
const LIMIT = 100;

/**
 * Операции журнала за интервал точки графика с расхождением (клик по пину
 * в TrendChart, окно `[ts(index−1), ts(index)]`). Непарные строки (по
 * сверке `matching.ts`, `minUsdt=0` — здесь важны все непарные, а не только
 * крупные) помечены ⚠ «пара не найдена» — вероятная причина скачка позиции.
 *
 * Паттерн модалки — как `ChatHistoryModal.tsx`: закрытие крестиком/Escape,
 * БЕЗ закрытия по клику на backdrop (уже реализовано в общем `Modal`).
 */
export function DivergenceModal({
  from,
  to,
  onClose,
}: {
  from: string;
  to: string;
  onClose: () => void;
}) {
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useBalanceChanges(LIMIT, {
    from,
    to,
  });
  // minUsdt=0 — здесь маркируем ЛЮБУЮ непарную строку окна, не только
  // крупную (в отличие от баннера/тултипа, где порог отсекает шум).
  const { data: unmatched } = useUnmatched(from, to, 0);

  const unmatchedIds = useMemo(() => new Set((unmatched?.items ?? []).map((r) => r.id)), [unmatched]);
  const items = data?.pages.flatMap((p) => p.items) ?? [];

  function openJournal() {
    onClose();
    localStorage.setItem(LS_KEY, '1');
    window.dispatchEvent(new Event(CHANGES_LOG_OPEN_EVENT));
    // rAF — ждём кадр после onClose (модалка снимает overflow:hidden с body
    // и скрывается), иначе scrollIntoView может отработать поверх ещё
    // заблокированного скролла.
    requestAnimationFrame(() => {
      document.getElementById(CHANGES_LOG_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  return (
    <Modal title={`Что произошло в ${fmtWindowRange(from, to)}`} onClose={onClose} size="lg">
      {isLoading && <div className="h-40 animate-pulse rounded bg-bg3" />}
      {!isLoading && items.length === 0 && (
        <div className="py-8 text-center text-sm text-text2">
          В журнале за этот интервал операций не найдено.
        </div>
      )}
      {!isLoading && items.length > 0 && (
        <div className="max-h-[60vh] overflow-y-auto rounded-md border border-border">
          <table className={tableCls}>
            <thead className={`${tableHead} sticky top-0`}>
              <tr>
                <th className={tableHeadCell}>Время</th>
                <th className={tableHeadCell}>Источник</th>
                <th className={tableHeadCell}>Чат</th>
                <th className={tableHeadCellRight}>Сумма</th>
                <th className={tableHeadCell}>Актор</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const isUnmatched = unmatchedIds.has(row.id);
                return (
                  <tr key={row.id} className={tableRow}>
                    <td
                      className={`${tableCell} text-xs text-text2`}
                      title={new Date(row.occurredAt).toLocaleString('ru-RU')}
                    >
                      {new Date(row.occurredAt).toLocaleTimeString('ru-RU', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className={tableCell}>
                      <span
                        className="rounded border border-border bg-bg3 px-1 py-px text-[9px] font-medium uppercase tracking-wide text-text2"
                        title={SOURCE_BADGE[row.source].title}
                      >
                        {SOURCE_BADGE[row.source].label}
                      </span>
                    </td>
                    <td className={tableCell} title={row.chatId}>
                      <span className="text-text">{row.title}</span>
                      {isUnmatched && (
                        <span
                          className="ml-1.5 rounded-md bg-[var(--red-bg)] px-1.5 py-0.5 text-[11px] font-medium text-danger"
                          title="Сверка не нашла для этой строки пару в журнале — вероятная причина расхождения"
                        >
                          ⚠ пара не найдена
                        </span>
                      )}
                    </td>
                    <td className={`${tableCellNum} ${deltaCls(row.source, row.delta)}`}>{fmtDelta(row)}</td>
                    <td className={`${tableCell} text-xs text-text2`} title={row.actor ?? undefined}>
                      {row.actor ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {hasNextPage && (
            <div className="py-2 text-center">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="text-xs font-medium text-accent hover:underline disabled:opacity-50"
              >
                {isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
              </button>
            </div>
          )}
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
        <AskAgentButton
          request={{
            kind: 'ask',
            question: 'Разбери, что произошло с позицией в этом интервале: какие операции её сдвинули, реальные ли это деньги и чего не хватает в учёте.',
            from: new Date(from).toISOString(),
            to: new Date(to).toISOString(),
          }}
        />
        <button type="button" onClick={openJournal} className={btnSecondarySm}>
          Открыть журнал
        </button>
      </div>
    </Modal>
  );
}

/** «HH:MM — HH:MM» (+ дата, если интервал пересекает сутки — редкость для окна из двух соседних точек). */
function fmtWindowRange(from: string, to: string): string {
  const f = new Date(from);
  const t = new Date(to);
  const time = (d: Date) => d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  const date = (d: Date) => d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  if (f.toDateString() === t.toDateString()) return `${time(f)} — ${time(t)}`;
  return `${date(f)} ${time(f)} — ${date(t)} ${time(t)}`;
}
